import {comparerEvenements} from '../../selectors/index.js';
import {etatPav} from '../../selectors/etat.js';
import {ACTE_COULEURS, PHOTO_ETIQUETTES, TIMELINE} from '../../tokens.js';

// Fonctions pures de l'écran Fiche PAV (PIL-01 lot 3). Aucune dépendance React.
// Entrée : `evts` = `index.evenementsParPav.get(pavId)` (triés date asc, PLANIFIÉ inclus).

const TYPES_PASSAGE = ['LAVAGE', 'IMPOSSIBLE', 'MAINT_PREV', 'GRAFFITI'];
const ORDRE_BLOCS = ['LAVAGE', 'MAINT_PREV', 'GRAFFITI'];

const cleGroupe = (e) => (e.interventionId != null ? `i${e.interventionId}` : `e${e.id}`);

function comparerGroupes(a, b) {
    // Plus récent d'abord : date, heure, puis interventionId décroissant (départage stable).
    const c = comparerEvenements(b, a);
    if (c !== 0) return c;
    return (Number(b.interventionId) || 0) - (Number(a.interventionId) || 0);
}

function grouperParPassage(evts) {
    const groupes = new Map();
    for (const e of evts || []) {
        if (!TYPES_PASSAGE.includes(e.type)) continue;
        const cle = cleGroupe(e);
        let g = groupes.get(cle);
        if (!g) groupes.set(cle, (g = {interventionId: e.interventionId ?? null, date: e.date, heure: e.heure || null, evenements: []}));
        g.evenements.push(e);
        if ((e.heure || '') > (g.heure || '')) g.heure = e.heure;
    }
    return [...groupes.values()];
}

/** R1 : passage le plus récent (date puis heure) = événements ERP partageant le même `interventionId`, ou `null`. */
export function dernierPassage(evts) {
    const passages = grouperParPassage(evts).sort(comparerGroupes);
    return passages.length > 0 ? passages[0] : null;
}

/** Verdict d'un passage, priorité impossible > problème > lavé. Un graffiti seul ne porte pas de verdict (`ni`). */
export function verdictPassage(passage) {
    const evts = passage.evenements;
    if (evts.some((e) => e.type === 'IMPOSSIBLE')) return 'imp';
    const verdicts = evts.filter((e) => e.type === 'LAVAGE' || e.type === 'MAINT_PREV').map((e) => e.verdict);
    if (verdicts.includes('probleme')) return 'pb';
    if (verdicts.length > 0) return 'ras';
    return 'ni';
}

const GRAVITE = {ni: 0, ras: 1, pb: 2, imp: 3};

/**
 * R2 (corrigée) : état courant du point = le pire entre l'état lavage et l'état maintenance à `jour`
 * (impossible > problème > lavé > non inspecté). `elementDefaut` = premier élément en défaut de l'état maintenance.
 */
export function verdictCourant(evts, jour, typologiesParPatrimoine = null) {
    const plage = {du: '', au: jour, at: jour};
    const lavage = etatPav(evts, {acte: 'lavage', ...plage});
    const maint = etatPav(evts, {acte: 'maint', ...plage}, typologiesParPatrimoine);
    const verdict = GRAVITE[lavage.verdict] >= GRAVITE[maint.verdict] ? lavage.verdict : maint.verdict;
    return {verdict, avecLavage: lavage.verdict !== 'ni', elementDefaut: maint.elementsEnDefaut[0] || null};
}

export function aLavage(passage) {
    return passage.evenements.some((e) => e.type === 'LAVAGE');
}

export function lavagePartiel(passage) {
    return passage.evenements.some((e) => e.type === 'LAVAGE' && e.detail && e.detail.partiel === true);
}

/** Titre du bandeau (vocabulaire handoff §1). */
export function titreBandeau(verdict, avecLavage) {
    if (verdict === 'imp') return 'Lavage impossible';
    if (verdict === 'pb') return avecLavage ? 'Lavé · maintenance nécessaire' : 'Maintenance nécessaire';
    if (verdict === 'ras') return avecLavage ? 'Lavé' : 'RAS';
    return 'Passage sans lavage';
}

/** Libellé de la pilule verdict de l'historique. */
export function libelleVerdictLigne(verdict, {avecLavage, partiel}) {
    if (verdict === 'imp') return 'Lavage impossible';
    if (verdict === 'pb') return avecLavage ? 'Lavé · maintenance nécessaire' : 'Maintenance nécessaire';
    if (verdict === 'ras') return avecLavage ? (partiel ? 'Lavé partiellement' : 'Lavé') : 'RAS';
    return '—';
}

/** R3 : blocs d'acte du passage, ordre Lavage, Maintenance préventive, Graffiti. Un passage IMPOSSIBLE = un seul bloc. */
export function blocsDuPassage(passage) {
    if (!passage) return [];
    const impossible = passage.evenements.find((e) => e.type === 'IMPOSSIBLE');
    if (impossible) {
        const blocs = [{type: 'IMPOSSIBLE', evenement: impossible}];
        const graffiti = passage.evenements.find((e) => e.type === 'GRAFFITI');
        if (graffiti) blocs.push({type: 'GRAFFITI', evenement: graffiti});
        return blocs;
    }
    const blocs = [];
    for (const type of ORDRE_BLOCS) {
        const evenement = passage.evenements.find((e) => e.type === type);
        if (evenement) blocs.push({type, evenement});
    }
    return blocs;
}

/** Premier élément PROBLEME du MAINT_PREV du passage, sinon `null`. */
export function elementEnDefaut(passage) {
    if (!passage) return null;
    const maint = passage.evenements.find((e) => e.type === 'MAINT_PREV');
    const el = maint ? (maint.elements || []).find((x) => x.statut === 'PROBLEME') : null;
    return el ? el.libelle : null;
}

/** Prochain passage = premier PLANIFIÉ du PAV (date asc), sinon `null`. */
export function prochainPassage(evts) {
    const p = (evts || []).find((e) => e.type === 'PLANIFIÉ');
    return p ? p.date : null;
}

export function dernierEvenementDeType(evts, type) {
    let res = null;
    for (const e of evts || []) if (e.type === type) res = e;
    return res;
}

/** Eau d'un lavage = nombreLaves x matrice[typologie], sinon `null`. */
export function eauDuLavage(lavage, matrice) {
    if (!lavage || !lavage.detail || !matrice) return null;
    const conso = matrice[lavage.detail.typologie];
    if (conso == null || lavage.detail.nombreLaves == null) return null;
    return lavage.detail.nombreLaves * conso;
}

/**
 * Rail « Lavage » : stats cumulées des LAVAGE du PAV. `null` sans lavage. Eau d'un lavage = `eauDuLavage` ; moyenne et cumul
 * sur les seuls lavages dont la conso est connue (`nbAvecConso`). `dernier.litres` : `null` si conso inconnue.
 */
export function statsLavage(evts, matrice) {
    const lavages = (evts || []).filter((e) => e.type === 'LAVAGE').sort(comparerEvenements);
    if (lavages.length === 0) return null;
    const litres = lavages.map((l) => eauDuLavage(l, matrice));
    const connus = litres.filter((v) => v != null);
    const eauCumulee = connus.reduce((a, b) => a + b, 0);
    const dernier = lavages[lavages.length - 1];
    return {
        nb: lavages.length,
        dernier: {date: dernier.date, litres: litres[litres.length - 1]},
        eauMoyenne: connus.length > 0 ? eauCumulee / connus.length : null,
        eauCumulee: connus.length > 0 ? eauCumulee : null,
        nbAvecConso: connus.length,
    };
}

const EST_COLLECTE_RAIL = (e) => e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE';

/**
 * Rail « Collecte » : stats des COLLECTE / COLLECTE_IMPOSSIBLE (déclassés exclus des moyennes). `null` sans événement.
 * `derniere` = dernier événement : si déclassé, `{date, declassee: true}` ; sinon `{date, kg, taux, declassee: false}`.
 */
export function statsCollecte(evts) {
    const tous = (evts || []).filter(EST_COLLECTE_RAIL).sort(comparerEvenements);
    if (tous.length === 0) return null;
    const somme = (t) => t.reduce((a, b) => a + b, 0);
    const normales = tous.filter((e) => e.type === 'COLLECTE');
    const kgs = normales.map((e) => (e.detail && e.detail.tonnageKg) || 0);
    const nbBacs = somme(normales.map((e) => (e.detail && e.detail.nbBacs) || 0));
    const taux = normales.map((e) => e.detail && e.detail.tauxRemplissage).filter((t) => typeof t === 'number');
    const dernier = tous[tous.length - 1];
    const d = dernier.detail || {};
    return {
        nb: normales.length,
        nbDeclasses: tous.length - normales.length,
        derniere: dernier.type === 'COLLECTE_IMPOSSIBLE'
            ? {date: dernier.date, kg: null, taux: null, declassee: true}
            : {date: dernier.date, kg: d.tonnageKg ?? null, taux: d.tauxRemplissage ?? null, declassee: false},
        tonnageCumule: somme(kgs),
        tonnageMoyen: nbBacs > 0 ? somme(kgs) / nbBacs : null, // kg par bac
        remplissageMoyen: taux.length > 0 ? somme(taux) / taux.length : null,
    };
}

/**
 * B1 : remplissage des `n` dernières collectes du PAV (ordre chronologique) : `[{x: date, y: taux}]`. Une collecte
 * déclassée (COLLECTE_IMPOSSIBLE) ou sans taux donne `y: null` (trou visible dans la courbe).
 */
export function serieCollectesPav(evts, n = 26) {
    return (evts || []).filter(EST_COLLECTE_RAIL).sort(comparerEvenements).slice(-n).map((e) => ({
        x: e.date,
        y: e.type === 'COLLECTE' && e.detail && typeof e.detail.tauxRemplissage === 'number' ? e.detail.tauxRemplissage : null,
    }));
}

/** B2 : eau (litres) des `n` derniers lavages du PAV (ordre chronologique) : `[{x: date, y: litres}]`, conso inconnue = `null`. */
export function serieLavagesPav(evts, matrice, n = 26) {
    return (evts || []).filter((e) => e.type === 'LAVAGE').sort(comparerEvenements).slice(-n).map((e) => ({x: e.date, y: eauDuLavage(e, matrice)}));
}

/** Photos d'un événement, au format lightbox `{src, label, color}`. Éléments de checklist : étiquette = libellé. */
export function photosEvenement(e) {
    const res = [];
    for (const p of e.photos || []) {
        const zoneLavage = e.type === 'LAVAGE' && p.etiquette !== 'AVANT' && p.etiquette !== 'APRES';
        res.push({
            src: p.url,
            label: p.etiquette === 'APRES' ? 'APRÈS' : p.etiquette,
            color: zoneLavage ? ACTE_COULEURS.lavage.rond : (PHOTO_ETIQUETTES[p.etiquette] || ACTE_COULEURS.probleme.encre),
        });
    }
    for (const el of e.elements || []) {
        for (const p of el.photos || []) {
            res.push({src: p.url, label: el.libelle, color: ACTE_COULEURS.maint.rond});
        }
    }
    return res;
}

const ordreEtiquette = {AVANT: 0, 'APRÈS': 1};

function libelleLavage(e, quantite) {
    const n = e.detail ? e.detail.nombreLaves : null;
    return n != null && quantite != null ? `Lavage ${n}/${quantite}` : 'Lavage';
}

function actesDuPassage(passage, quantite) {
    const actes = [];
    for (const e of passage.evenements) {
        if (e.type === 'LAVAGE') actes.push({type: 'lavage', libelle: libelleLavage(e, quantite)});
        else if (e.type === 'MAINT_PREV') {
            const defauts = (e.elements || []).filter((x) => x.statut === 'PROBLEME').map((x) => x.libelle.toLowerCase());
            actes.push({type: 'maint', libelle: defauts.length > 0 ? `Maintenance · ${defauts.join(', ')}` : 'Maintenance'});
        } else if (e.type === 'GRAFFITI') actes.push({type: 'graffiti', libelle: 'Graffiti'});
        else if (e.type === 'IMPOSSIBLE') {
            const motif = (e.motifs || []).join(', ');
            actes.push({type: 'probleme', libelle: `Motif : ${motif || '—'}`});
        }
    }
    const rang = {lavage: 0, maint: 1, graffiti: 2, probleme: 3};
    return actes.sort((a, b) => rang[a.type] - rang[b.type]);
}

const EST_COLLECTE = (e) => e.type === 'COLLECTE' || e.type === 'COLLECTE_IMPOSSIBLE';

/** « Collecte · 74 kg · 2 bacs » ; déclassé : « Collecte impossible · bac déclassé ». */
function actesCollecte(e) {
    if (e.type === 'COLLECTE_IMPOSSIBLE') return [{type: 'collecteImpossible', libelle: 'Collecte impossible · bac déclassé'}];
    const d = e.detail || {};
    const morceaux = ['Collecte'];
    if (d.tonnageKg != null) morceaux.push(`${Math.round(d.tonnageKg).toLocaleString('fr-FR')} kg`);
    if (d.nbBacs != null) morceaux.push(`${d.nbBacs} bac${d.nbBacs > 1 ? 's' : ''}`);
    return [{type: 'collecte', libelle: morceaux.join(' · ')}];
}

const MS_JOUR = 86400000;

/**
 * Tonnage des 4 dernières semaines (rail « Détails › Tonnage ») : somme des COLLECTE dont la date est dans les 28 jours
 * qui précèdent `jour` (jour compris). `null` quand le PAV n'a aucun événement de collecte (affiché « — kg »).
 */
export function tonnage4Semaines(evts, jour) {
    const collectes = (evts || []).filter(EST_COLLECTE);
    if (collectes.length === 0) return null;
    const debut = new Date(Date.parse(`${jour}T00:00:00Z`) - 28 * MS_JOUR).toISOString().slice(0, 10);
    let somme = 0;
    for (const e of collectes) {
        if (e.type === 'COLLECTE' && e.date > debut && e.date <= jour) somme += (e.detail && e.detail.tonnageKg) || 0;
    }
    return somme;
}

/**
 * R5 : lignes de l'historique. PLANIFIÉ en tête (date croissante), puis passages (un par `interventionId`), du plus récent au plus ancien. `quantite` = nombre d'abri-bacs du PAV (dénominateur de « Lavage n/N »).
 * Ligne : `{cle, nature: 'planifie'|'passage', date, heure, interventionId, verdict, avecLavage, partiel,
 * actes[{type, libelle}], photos[{src, label, color}], evenements[]}`.
 */
export function lignesHistorique(evts, quantite = null) {
    const planifies = [];
    const autres = [];
    for (const e of evts || []) {
        if (e.type === 'PLANIFIÉ') {
            planifies.push({cle: `p${e.id}`, nature: 'planifie', date: e.date, heure: null, interventionId: null, verdict: 'ni',
                avecLavage: false, partiel: false, actes: [{type: 'planifie', libelle: 'Lavage'}], photos: [], evenements: [e]});
        }
    }
    for (const g of grouperParPassage(evts)) {
        const photos = g.evenements.flatMap(photosEvenement)
            .sort((a, b) => (ordreEtiquette[a.label] ?? 9) - (ordreEtiquette[b.label] ?? 9));
        autres.push({
            cle: cleGroupe(g.evenements[0]), nature: 'passage', date: g.date, heure: g.heure, interventionId: g.interventionId,
            verdict: verdictPassage(g), avecLavage: aLavage(g), partiel: lavagePartiel(g),
            actes: actesDuPassage(g, quantite), photos, evenements: g.evenements,
        });
    }
    // Collecte simulée (lot 8) : une ligne par événement, mêlée aux passages par date. Absente quand le bouton est à 0.
    for (const e of evts || []) {
        if (!EST_COLLECTE(e)) continue;
        autres.push({
            cle: `c${e.id}`, nature: 'collecte', date: e.date, heure: null, interventionId: null,
            verdict: e.type === 'COLLECTE_IMPOSSIBLE' ? 'imp' : 'ras', avecLavage: false, partiel: false,
            actes: actesCollecte(e), photos: [], evenements: [e],
        });
    }
    planifies.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    // Plus récent d'abord ; à date égale, départage par intervention.
    autres.sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? 1 : -1;
        const ha = a.heure || '';
        const hb = b.heure || '';
        if (ha !== hb) return ha < hb ? 1 : -1;
        return (Number(b.interventionId) || 0) - (Number(a.interventionId) || 0);
    });
    return [...planifies, ...autres];
}

/** Filtre par acte : `tout` | `lavages` (lavage + impossible + planifié) | `maintenance` | `graffitis` | `collecte`. */
export function filtrerLignes(lignes, filtre) {
    if (filtre === 'tout') return lignes;
    const types = {lavages: ['lavage', 'probleme', 'planifie'], maintenance: ['maint'], graffitis: ['graffiti'], collecte: ['collecte', 'collecteImpossible']}[filtre] || [];
    return lignes.filter((l) => l.actes.some((a) => types.includes(a.type)));
}

export const FLUX_LIBELLES = {BIO: 'Biodéchets', VER: 'Verre', OM: 'Ordures ménagères', REC: 'Recyclables', PAP: 'Papier'};
export const GROUPE_LIBELLES = {intra: 'Intramuros', extra: 'Extramuros'};
export const STATUT_LIBELLES = {ACTIF: 'Actif', RETIRÉ: 'Retiré'};

/** AAAA-MM-JJ -> JJ/MM/AAAA ; absent -> « — ». */
export function formatDate(d) {
    if (!d || !/^\d{4}-\d{2}-\d{2}/.test(d)) return '—';
    return `${d.slice(8, 10)}/${d.slice(5, 7)}/${d.slice(0, 4)}`;
}

/** Icône d'une ligne d'historique : l'événement le plus grave (impossible > maintenance > lavage > graffiti). */
export function iconeLigne(ligne) {
    if (ligne.nature === 'planifie') return {type: 'planifie', couleur: TIMELINE.planifie};
    if (ligne.nature === 'collecte') return {type: 'collecte', couleur: ligne.verdict === 'imp' ? TIMELINE.impossible : TIMELINE.collecte};
    const types = new Set((ligne.actes || []).map((a) => a.type));
    let type = 'lavage';
    if (types.has('probleme')) type = 'impossible';
    else if (types.has('maint')) type = 'maintenance';
    else if (types.has('lavage')) type = 'lavage';
    else if (types.has('graffiti')) type = 'graffiti';
    return {type, couleur: TIMELINE[type]};
}
